import React, { useCallback, useMemo, useRef, useState } from 'react';
import {
  Upload,
  Wand2,
  Download,
  Loader2,
  X,
  Sparkles,
  AlertCircle,
  Eraser,
  Palette,
  PenLine,
  CheckCircle2,
  Hand,
  RefreshCw,
  Plus,
  Archive,
  Trash2,
} from 'lucide-react';

const API_BASE = 'https://api.glowytics.com';

// Tüm preset'lerin sonuna otomatik eklenen ortak iyileştirme paketi.
const DEFAULT_IMPROVEMENTS = `

OTOMATİK İYİLEŞTİRMELER (her durumda uygula):
- Arka plan saf, eşit, kirsiz beyaz (#FFFFFF) olsun.
- Beyaz dengesi nötr, pozlama dengeli, gölgeler yumuşak.
- Aşırı doygunluk, "filtre" görünümü, yapay parlaklık yok.
- Doku, dikiş, malzeme detayları gerçekçi ölçüde net (aşırı keskinleştirme yok).
- Ürün altında doğal yumuşak kontak gölgesi olsun.
- Rugan / kristal / jel yüzeylerdeki çok sert yansımalar hafifçe yumuşatılsın (doğal parlaklık korunsun).
- Sonuç premium e-ticaret stüdyo standardında görünmeli.

KORUNACAKLAR (kesinlikle):
- Ürünün rengi, formu, dokusu, malzemesi, marka detayları, oranları aynen kalsın.
- Görselin kompozisyonu, çerçevelemesi ve ürünün konumu birebir aynı.
- Çıktı en-boy oranı girdi görselle aynı.
- Gerçekçi fotoğraf izlenimi — AI üretim izi yok.`;

const COLOR_ROSE = { bg: 'bg-rose-50', ring: 'ring-rose-200', text: 'text-rose-700', hover: 'hover:bg-rose-100', iconBg: 'bg-rose-100' };
const COLOR_ORANGE = { bg: 'bg-orange-50', ring: 'ring-orange-200', text: 'text-orange-700', hover: 'hover:bg-orange-100', iconBg: 'bg-orange-100' };
const COLOR_EMERALD = { bg: 'bg-emerald-50', ring: 'ring-emerald-200', text: 'text-emerald-700', hover: 'hover:bg-emerald-100', iconBg: 'bg-emerald-100' };

const PRESETS = [
  {
    id: 'remove_holder',
    label: 'Plastik tutucu / stand kaldır',
    icon: Eraser, color: COLOR_ROSE,
    description: 'Ayakkabının arkasında / içinde fotoğraf çekimi için konulmuş şeffaf plastik destek aparatlarını kaldırır. Ürünün TASARIM parçası olan şeffaf kayışlara DOKUNMAZ.',
    cleanup: `GÖREV: Bu profesyonel ürün fotoğrafında SADECE fotoğraf çekimi için konulmuş, ürünün tasarımıyla ilgisi olmayan şeffaf plastik destek/tutucu/stant aparatlarını kaldır.

NE KALDIRILIR (sadece bunlar):
- Ayakkabının arkasından, ÜRÜNÜN ARKASINA YERLEŞTİRİLMİŞ, ürüne dokunmadan onu dik tutan şeffaf plastik destek çubuğu / blok
- Ayakkabının içine yerleştirilmiş, AYAĞIN ŞEKLİNİ taklit eden plastik form/şekillendirici (eğer varsa)
- Ürünün üstünde "yapışık değilmiş" gibi duran, geometrik/dikdörtgen şekilli açıkça yardımcı parça olduğu belli olan plastik klipsler

ASLA KALDIRMA (bunlar ürünün TASARIM PARÇASI):
- Ürünün kendisine ait şeffaf vinil / PVC / plastik kayışlar, bantlar, kemerler — bunlar ayağa giyildiğinde ayağı saran tasarım elemanlarıdır
- Şeffaf X / V / çapraz desenli kayışlar (ayakkabının modeli budur)
- Ürünle aynı eğimi, kıvrımı, çizgiyi takip eden tüm şeffaf parçalar
- Tabanla, deri/saten gövdeyle, topukla bağlantısı olan tüm şeffaf parçalar

EMİN DEĞİLSEN: O parçayı OLDUĞU GİBİ BIRAK. Ürünün tasarımına asla dokunma. Sadece açıkça "fotoğraf çekim aparatı" olduğu net olan şeyleri kaldır. Hiç tutucu yoksa hiçbir şey kaldırma — sadece otomatik iyileştirmeleri yap.

Kaldırdığın yerler ürünün doğal iç astarı, mevcut malzemesi veya temiz beyaz arka plan ile mantıklı şekilde tamamlanmalı.`,
  },
  {
    id: 'remove_props',
    label: 'El / ip / kanca kaldır',
    icon: Hand, color: COLOR_ORANGE,
    description: 'Ürünü tutan el, parmak, ip, kanca, klips, çengel gibi DIŞ objeler temizlenir. Ürünün kendisine dokunulmaz.',
    cleanup: `GÖREV: Bu ürün fotoğrafında ürünü taşımak veya sergilemek için kullanılan, ÜRÜNE AİT OLMAYAN dış objeleri kaldır.

KALDIRILACAKLAR:
- Ürünü tutan insan eli, parmak, kol
- Asılı tutan ip, balıkçı naylonu, tel, kanca, çengel, klips, mandal
- Ürünün altındaki/yanındaki destek blokları, taş, masa kenarı, sergi tutacağı

ASLA KALDIRMA:
- Ürünün kendi parçaları (kayış, fiyonk, taş, aksesuar, etiket, dikiş)
- Ürünün tasarımına dahil herhangi bir bant, kemer, ip (örn. ayakkabı bağcığı, çanta sapı, kemer tokası)

EMİN DEĞİLSEN: Bırak, dokunma. Hiç dış obje yoksa sadece otomatik iyileştirmeleri yap.

Kaldırılan bölgeler ürünün doğal devamı veya temiz beyaz arka plan ile yeniden oluşturulmalı.`,
  },
  {
    id: 'cleanup_marks',
    label: 'Toz / iz / kırışıklık temizle',
    icon: Palette, color: COLOR_EMERALD,
    description: 'Üründeki tozlar, parmak izi, taşıma izi, hafif kırışıklıklar silinir; doğal doku ve tasarım korunur.',
    cleanup: `GÖREV: Bu ürün fotoğrafında ürünün üzerinde görünen, ÜRÜNE AİT OLMAYAN küçük yüzeysel kusurları temizle.

TEMİZLENECEKLER:
- Toz parçacıkları, tüy, lif
- Taşıma / paketleme izleri, parmak izleri, hafif lekeler
- Çok hafif yüzey kırışıklıkları (deride / saten kumaşta)

ASLA DOKUNMA:
- Dikişler, doğal hatlar, kıvrımlar
- Ürünün tasarımına ait dokular, desenler, baskılar
- Doğal malzeme yapısı (deri damarı, saten parlaması, jelly şeffaflığı)

Aşırı pürüzsüzleştirme yapma — fotoğraf gerçekçi ve premium kalmalı. Hiç kusur yoksa sadece otomatik iyileştirmeleri yap.`,
  },
];

async function loadImageMeta(file) {
  const url = URL.createObjectURL(file);
  try {
    const img = await new Promise((resolve, reject) => {
      const i = new Image();
      i.onload = () => resolve(i);
      i.onerror = () => reject(new Error('Görsel okunamadı'));
      i.src = url;
    });
    return {
      img,
      naturalWidth: img.naturalWidth || img.width,
      naturalHeight: img.naturalHeight || img.height,
    };
  } finally {
    URL.revokeObjectURL(url);
  }
}

async function buildPayloadAndPreview(file, maxSide = 1600, quality = 0.92) {
  const { img, naturalWidth, naturalHeight } = await loadImageMeta(file);
  const scale = Math.min(1, maxSide / Math.max(naturalWidth, naturalHeight));
  const cw = Math.max(1, Math.round(naturalWidth * scale));
  const ch = Math.max(1, Math.round(naturalHeight * scale));
  const canvas = document.createElement('canvas');
  canvas.width = cw;
  canvas.height = ch;
  const ctx = canvas.getContext('2d');
  ctx.drawImage(img, 0, 0, cw, ch);
  const dataUrl = canvas.toDataURL('image/jpeg', quality);
  const base64 = dataUrl.split(',')[1] || '';
  return {
    payload: { mimeType: 'image/jpeg', data: base64 },
    preview: dataUrl,
    originalWidth: naturalWidth,
    originalHeight: naturalHeight,
    sentWidth: cw,
    sentHeight: ch,
  };
}

function aspectRatioFor(w, h) {
  const r = w / h;
  const candidates = [
    { v: '1:1', r: 1 },
    { v: '3:4', r: 3 / 4 },
    { v: '4:3', r: 4 / 3 },
    { v: '9:16', r: 9 / 16 },
    { v: '16:9', r: 16 / 9 },
  ];
  let best = candidates[0];
  let bestDiff = Math.abs(Math.log(r / best.r));
  for (const c of candidates) {
    const d = Math.abs(Math.log(r / c.r));
    if (d < bestDiff) { bestDiff = d; best = c; }
  }
  return best.v;
}

function sampleBackgroundColor(img) {
  const tmp = document.createElement('canvas');
  tmp.width = img.width;
  tmp.height = img.height;
  const tctx = tmp.getContext('2d');
  tctx.drawImage(img, 0, 0);

  const cornerSize = Math.max(8, Math.round(Math.min(img.width, img.height) * 0.03));
  const cw = Math.min(cornerSize, img.width);
  const ch = Math.min(cornerSize, img.height);
  const corners = [
    [0, 0],
    [img.width - cw, 0],
    [0, img.height - ch],
    [img.width - cw, img.height - ch],
  ];

  // Use median per channel across corners to resist a single corner where the
  // product overlaps the edge.
  const rs = [], gs = [], bs = [];
  for (const [x, y] of corners) {
    try {
      const data = tctx.getImageData(x, y, cw, ch).data;
      let sr = 0, sg = 0, sb = 0, n = 0;
      for (let i = 0; i < data.length; i += 4) {
        sr += data[i]; sg += data[i + 1]; sb += data[i + 2]; n++;
      }
      if (n) { rs.push(sr / n); gs.push(sg / n); bs.push(sb / n); }
    } catch (_) { /* tainted canvas — skip */ }
  }
  if (!rs.length) return '#FFFFFF';
  const median = (arr) => {
    const s = [...arr].sort((a, b) => a - b);
    const m = Math.floor(s.length / 2);
    return s.length % 2 ? s[m] : (s[m - 1] + s[m]) / 2;
  };
  const r = Math.round(median(rs));
  const g = Math.round(median(gs));
  const b = Math.round(median(bs));
  return `rgb(${r}, ${g}, ${b})`;
}

async function fitToOriginalSize(resultBase64, resultMime, targetW, targetH) {
  const img = await new Promise((resolve, reject) => {
    const i = new Image();
    i.onload = () => resolve(i);
    i.onerror = () => reject(new Error('Sonuç görseli açılamadı'));
    i.src = `data:${resultMime || 'image/png'};base64,${resultBase64}`;
  });
  const canvas = document.createElement('canvas');
  canvas.width = targetW;
  canvas.height = targetH;
  const ctx = canvas.getContext('2d');
  // Use the model's actual background color (from corners) so letterbox bars
  // blend with the result instead of leaving white bands when the model
  // returns a non-white background.
  ctx.fillStyle = sampleBackgroundColor(img);
  ctx.fillRect(0, 0, targetW, targetH);
  const srcA = img.width / img.height;
  const dstA = targetW / targetH;
  let dw, dh, dx, dy;
  if (Math.abs(srcA - dstA) < 0.01) {
    dw = targetW; dh = targetH; dx = 0; dy = 0;
  } else if (srcA > dstA) {
    dw = targetW; dh = Math.round(targetW / srcA); dx = 0; dy = Math.round((targetH - dh) / 2);
  } else {
    dh = targetH; dw = Math.round(targetH * srcA); dy = 0; dx = Math.round((targetW - dw) / 2);
  }
  ctx.drawImage(img, dx, dy, dw, dh);
  const outDataUrl = canvas.toDataURL('image/jpeg', 0.95);
  return { dataUrl: outDataUrl, base64: outDataUrl.split(',')[1] || '', mimeType: 'image/jpeg' };
}

function uid() {
  return Math.random().toString(36).slice(2, 10) + Date.now().toString(36).slice(-4);
}

const STATUS_BADGE = {
  idle: { text: 'Bekliyor', cls: 'bg-gray-100 text-gray-600' },
  queued: { text: 'Sırada', cls: 'bg-amber-50 text-amber-700' },
  processing: { text: 'İşleniyor', cls: 'bg-violet-50 text-violet-700' },
  done: { text: 'Tamam', cls: 'bg-emerald-50 text-emerald-700' },
  error: { text: 'Hata', cls: 'bg-red-50 text-red-700' },
};

export default function ImageEditor() {
  const [model, setModel] = useState('gemini-2.5-flash-image');
  // Her item: { id, source: {...} | null, result: {...} | null, status, error, name }
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activePresetId, setActivePresetId] = useState(null);
  const [globalError, setGlobalError] = useState('');
  const [showCustom, setShowCustom] = useState(false);
  const [customPrompt, setCustomPrompt] = useState('');
  const [zipping, setZipping] = useState(false);

  const fileInputRef = useRef(null);
  const dragCounter = useRef(0);
  const [isDragging, setIsDragging] = useState(false);

  const brandId = useMemo(() => {
    try {
      return (
        localStorage.getItem('currentBrandId') ||
        localStorage.getItem('selectedBrandId') ||
        sessionStorage.getItem('currentBrandId') ||
        sessionStorage.getItem('selectedBrandId') ||
        ''
      );
    } catch (_) {
      return '';
    }
  }, []);

  const counts = useMemo(() => {
    const total = items.length;
    let done = 0, processing = 0, queued = 0, error = 0, idle = 0;
    for (const it of items) {
      if (it.status === 'done') done++;
      else if (it.status === 'processing') processing++;
      else if (it.status === 'queued') queued++;
      else if (it.status === 'error') error++;
      else idle++;
    }
    return { total, done, processing, queued, error, idle };
  }, [items]);

  const addFiles = useCallback(async (fileList) => {
    setGlobalError('');
    const valid = Array.from(fileList || []).filter((f) => String(f.type || '').startsWith('image/'));
    const invalidCount = (fileList?.length || 0) - valid.length;
    if (!valid.length) {
      setGlobalError('Lütfen görsel dosyası seçin (PNG / JPG / WebP).');
      return;
    }
    if (invalidCount > 0) {
      setGlobalError(`${invalidCount} dosya görsel olmadığı için atlandı.`);
    }
    const built = await Promise.all(valid.map(async (f) => {
      try {
        const b = await buildPayloadAndPreview(f, 1600, 0.92);
        return {
          id: uid(),
          name: f.name,
          source: { ...b, name: f.name, size: f.size },
          result: null,
          status: 'idle',
          error: '',
        };
      } catch (e) {
        return {
          id: uid(),
          name: f.name,
          source: null,
          result: null,
          status: 'error',
          error: e?.message || 'Görsel işlenemedi.',
        };
      }
    }));
    setItems((prev) => [...prev, ...built]);
  }, []);

  const onFileInputChange = (e) => {
    const fs = e.target.files;
    if (fs && fs.length) addFiles(fs);
    e.target.value = '';
  };
  const onDragEnter = (e) => { e.preventDefault(); e.stopPropagation(); dragCounter.current += 1; setIsDragging(true); };
  const onDragLeave = (e) => {
    e.preventDefault(); e.stopPropagation();
    dragCounter.current -= 1;
    if (dragCounter.current <= 0) { dragCounter.current = 0; setIsDragging(false); }
  };
  const onDragOver = (e) => { e.preventDefault(); e.stopPropagation(); };
  const onDrop = (e) => {
    e.preventDefault(); e.stopPropagation();
    dragCounter.current = 0; setIsDragging(false);
    const fs = e.dataTransfer?.files;
    if (fs && fs.length) addFiles(fs);
  };

  const removeItem = useCallback((id) => {
    setItems((prev) => prev.filter((it) => it.id !== id));
  }, []);

  const clearAll = useCallback(() => {
    if (loading) return;
    setItems([]);
    setActivePresetId(null);
    setGlobalError('');
  }, [loading]);

  const applyResultAsSource = useCallback((id) => {
    setItems((prev) => prev.map((it) => {
      if (it.id !== id || !it.result || !it.source) return it;
      return {
        ...it,
        source: {
          ...it.source,
          payload: { mimeType: 'image/jpeg', data: it.result.data },
          preview: it.result.dataUrl,
          sentWidth: it.source.originalWidth,
          sentHeight: it.source.originalHeight,
          name: `${(it.source.name || 'edited').replace(/\.[a-z0-9]+$/i, '')}_iter.jpg`,
        },
        result: null,
        status: 'idle',
      };
    }));
  }, []);

  const downloadOne = useCallback((item) => {
    if (!item?.result) return;
    const a = document.createElement('a');
    a.href = item.result.dataUrl;
    const baseName = (item.source?.name || 'product').replace(/\.[a-z0-9]+$/i, '');
    a.download = `${baseName}_${item.result.presetId}_glowytics.jpg`;
    a.click();
  }, []);

  const downloadAllZip = useCallback(async () => {
    const done = items.filter((it) => it.result);
    if (!done.length) return;
    setZipping(true);
    try {
      const mod = await import('jszip');
      const JSZip = mod.default || mod;
      const zip = new JSZip();
      const usedNames = new Set();
      for (const it of done) {
        const baseName = (it.source?.name || 'product').replace(/\.[a-z0-9]+$/i, '');
        let fileName = `${baseName}_${it.result.presetId}_glowytics.jpg`;
        let n = 1;
        while (usedNames.has(fileName)) {
          fileName = `${baseName}_${it.result.presetId}_glowytics_${n++}.jpg`;
        }
        usedNames.add(fileName);
        zip.file(fileName, it.result.data, { base64: true });
      }
      const blob = await zip.generateAsync({ type: 'blob' });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `glowytics_image_editor_${Date.now()}.zip`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(a.href), 1000);
    } catch (e) {
      setGlobalError(e?.message || 'ZIP oluşturulamadı.');
    } finally {
      setZipping(false);
    }
  }, [items]);

  const runPreset = useCallback(async (preset, opts = {}) => {
    if (loading) return;
    const targets = items.filter((it) => it.source);
    if (!targets.length) {
      setGlobalError('Önce en az bir görsel yükle.');
      return;
    }
    const cleanup = opts.customText ? opts.customText : (preset.cleanup || '');
    const promptText = [cleanup, DEFAULT_IMPROVEMENTS].filter((s) => s && s.trim()).join('\n\n');
    if (!promptText.trim()) {
      setGlobalError('Prompt boş olamaz.');
      return;
    }

    setGlobalError('');
    setLoading(true);
    setActivePresetId(preset.id);

    const targetIds = new Set(targets.map((t) => t.id));
    setItems((prev) => prev.map((it) => targetIds.has(it.id)
      ? { ...it, status: 'queued', error: '', result: null }
      : it
    ));

    const concurrency = 2;
    let idx = 0;
    const runOne = async (target) => {
      setItems((prev) => prev.map((it) => it.id === target.id ? { ...it, status: 'processing' } : it));
      try {
        const aspectRatio = aspectRatioFor(target.source.originalWidth, target.source.originalHeight);
        const resp = await fetch(`${API_BASE}/api/ai/gemini/chat`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json', 'X-Brand-ID': brandId },
          body: JSON.stringify({
            model,
            aspectRatio,
            temperature: 0.4,
            messages: [
              {
                role: 'user',
                content: promptText,
                images: [target.source.payload],
              },
            ],
          }),
        });
        const txt = await resp.text();
        let json;
        try { json = JSON.parse(txt); } catch { json = { message: txt }; }
        if (!resp.ok) {
          const detailsStr = (() => {
            const d = json?.details;
            if (!d) return '';
            if (typeof d === 'string') return d.slice(0, 500);
            try { return JSON.stringify(d).slice(0, 500); } catch { return ''; }
          })();
          throw new Error([json?.error, detailsStr].filter(Boolean).join(' — ') || resp.statusText);
        }
        const images = Array.isArray(json?.images) ? json.images : [];
        if (!images.length) {
          const modelText = (json?.message || '').trim();
          throw new Error(modelText
            ? `Model görsel yerine yazı döndü: "${modelText.slice(0, 220)}". Modeli "Pro" ile dene.`
            : 'Model görsel üretmedi. Modeli "Pro" ile dene veya başka bir aksiyon seç.');
        }
        const first = images[0];
        const fitted = await fitToOriginalSize(
          first.data,
          first.mimeType || 'image/png',
          target.source.originalWidth,
          target.source.originalHeight,
        );
        const out = {
          mimeType: fitted.mimeType,
          data: fitted.base64,
          dataUrl: fitted.dataUrl,
          message: json?.message || '',
          presetId: preset.id,
          presetLabel: preset.label,
        };
        setItems((prev) => prev.map((it) => it.id === target.id
          ? { ...it, status: 'done', result: out, error: '' }
          : it));
      } catch (e) {
        setItems((prev) => prev.map((it) => it.id === target.id
          ? { ...it, status: 'error', error: e?.message || 'Görsel üretilemedi.' }
          : it));
      }
    };

    const workers = Array.from({ length: Math.min(concurrency, targets.length) }, async () => {
      while (idx < targets.length) {
        const myIdx = idx++;
        await runOne(targets[myIdx]);
      }
    });
    await Promise.all(workers);
    setLoading(false);
  }, [items, loading, model, brandId]);

  const hasItems = items.length > 0;

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      <div className="max-w-6xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="mb-6">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-violet-50 text-violet-700 text-xs font-semibold ring-1 ring-violet-200 mb-3">
            <Sparkles className="w-3.5 h-3.5" />
            Gemini · Ürün Görseli İyileştirici
          </div>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900">Ürün Fotoğrafı Düzeltme</h1>
          <p className="mt-2 text-gray-600 text-sm max-w-2xl leading-relaxed">
            Birden fazla ürün fotoğrafını orijinal boyutunda toplu olarak iyileştir. Görselleri yükle,
            tek aksiyon seç — hepsi aynı temizlemeyle işlenir, çıktıları tek tek veya ZIP olarak indir.
            Her çıktı <strong>girdi görselle aynı en-boy oranı ve boyutta</strong> gelir.
          </p>
        </div>

        {/* Upload area */}
        <div
          onDragEnter={onDragEnter}
          onDragLeave={onDragLeave}
          onDragOver={onDragOver}
          onDrop={onDrop}
          className={`bg-white rounded-2xl ring-1 shadow-sm mb-5 transition-all ${isDragging ? 'ring-2 ring-violet-300 bg-violet-50' : 'ring-gray-200'}`}
        >
          {!hasItems ? (
            <button
              onClick={() => fileInputRef.current?.click()}
              className="w-full flex flex-col items-center justify-center gap-3 py-12 text-gray-400 hover:text-violet-600 transition-colors"
            >
              <Upload className="w-10 h-10" />
              <div className="text-center">
                <p className="text-sm font-medium">Görselleri yükle (birden fazla seçebilirsin)</p>
                <p className="text-xs">veya buraya sürükle bırak (PNG / JPG / WebP)</p>
              </div>
            </button>
          ) : (
            <div className="px-4 py-3 flex flex-wrap items-center gap-3">
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={loading}
                className="inline-flex items-center gap-1.5 text-xs font-semibold bg-violet-600 hover:bg-violet-700 disabled:bg-gray-300 text-white px-3 py-1.5 rounded-lg"
              >
                <Plus className="w-3.5 h-3.5" /> Görsel ekle
              </button>
              <div className="text-xs text-gray-600 flex flex-wrap items-center gap-x-3 gap-y-1">
                <span><strong>{counts.total}</strong> görsel</span>
                {counts.done > 0 && <span className="text-emerald-700">{counts.done} tamam</span>}
                {counts.processing > 0 && <span className="text-violet-700">{counts.processing} işleniyor</span>}
                {counts.queued > 0 && <span className="text-amber-700">{counts.queued} sırada</span>}
                {counts.idle > 0 && <span className="text-gray-500">{counts.idle} bekliyor</span>}
                {counts.error > 0 && <span className="text-red-600">{counts.error} hata</span>}
              </div>
              <div className="ml-auto flex items-center gap-2">
                {counts.done > 0 && (
                  <button
                    onClick={downloadAllZip}
                    disabled={zipping}
                    className="inline-flex items-center gap-1.5 text-xs font-semibold bg-emerald-600 hover:bg-emerald-700 disabled:bg-gray-300 text-white px-3 py-1.5 rounded-lg"
                  >
                    {zipping ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Archive className="w-3.5 h-3.5" />}
                    Hepsini ZIP indir ({counts.done})
                  </button>
                )}
                <button
                  onClick={clearAll}
                  disabled={loading}
                  className="inline-flex items-center gap-1.5 text-xs font-medium text-gray-500 hover:text-red-600 disabled:opacity-50 px-2 py-1.5"
                  title="Tüm görselleri temizle"
                >
                  <Trash2 className="w-3.5 h-3.5" /> Temizle
                </button>
              </div>
            </div>
          )}
          <input ref={fileInputRef} type="file" accept="image/*" multiple onChange={onFileInputChange} className="hidden" />
        </div>

        {/* Action Presets */}
        <div className="bg-white rounded-2xl ring-1 ring-gray-200 shadow-sm p-5 mb-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-gray-800">
              Aksiyon Seç {hasItems && <span className="text-xs font-normal text-gray-500">— {counts.total} görsele uygulanır</span>}
            </h3>
            <button
              onClick={() => setShowCustom((v) => !v)}
              className="text-xs font-medium text-violet-600 hover:text-violet-800 inline-flex items-center gap-1"
            >
              <PenLine className="w-3.5 h-3.5" />
              {showCustom ? 'Hazır aksiyonlara dön' : 'Özel prompt yaz'}
            </button>
          </div>

          {!showCustom ? (
            <div>
              <p className="text-[11px] text-gray-500 mb-3">
                Tüm aksiyonlar otomatik olarak <strong>renk &amp; ışık düzeltme, beyaz arka plan, doğal gölge,
                netleştirme ve premium stüdyo görünümü</strong> uygular. Aksiyona tıkladığında <strong>yüklü olan tüm görsellere</strong> uygulanır
                (aynı anda 2 görsel paralel işlenir).
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {PRESETS.map((p) => {
                  const Icon = p.icon;
                  const isActive = activePresetId === p.id && loading;
                  return (
                    <button
                      key={p.id}
                      onClick={() => runPreset(p)}
                      disabled={loading || !hasItems}
                      className={`text-left ${p.color.bg} ${p.color.hover} ring-1 ${p.color.ring} disabled:opacity-50 disabled:cursor-not-allowed rounded-xl p-4 transition-all`}
                    >
                      <div className="flex items-center gap-2 mb-2">
                        <div className={`w-9 h-9 rounded-lg ${p.color.iconBg} ${p.color.text} flex items-center justify-center`}>
                          {isActive ? <Loader2 className="w-4 h-4 animate-spin" /> : <Icon className="w-4 h-4" />}
                        </div>
                        <p className={`text-sm font-semibold ${p.color.text}`}>{p.label}</p>
                      </div>
                      <p className="text-[11px] text-gray-600 leading-relaxed">{p.description}</p>
                    </button>
                  );
                })}
              </div>
            </div>
          ) : (
            <div>
              <textarea
                value={customPrompt}
                onChange={(e) => setCustomPrompt(e.target.value)}
                placeholder="Örn: Sadece ürün altındaki gölgeyi biraz daha yumuşat. Ürünü değiştirme, kompozisyonu koru."
                rows={4}
                className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 outline-none focus:ring-2 focus:ring-violet-200 resize-none"
              />
              <div className="mt-3 flex items-center justify-between">
                <p className="text-[11px] text-gray-500">
                  Çekirdek koruma kuralları (boyut, ürün dokunulmazlığı) prompt'a otomatik eklenir.
                  Tüm yüklü görsellere uygulanır.
                </p>
                <button
                  onClick={() => runPreset(
                    { id: 'custom', label: 'Özel', cleanup: '' },
                    { customText: `Görevin: ${customPrompt}` }
                  )}
                  disabled={loading || !hasItems || !customPrompt.trim()}
                  className="inline-flex items-center gap-2 bg-violet-600 hover:bg-violet-700 disabled:bg-gray-300 text-white text-sm font-semibold px-4 py-2 rounded-lg shadow-sm"
                >
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4" />}
                  Çalıştır
                </button>
              </div>
            </div>
          )}

          {/* Model toggle */}
          <div className="mt-4 pt-4 border-t border-gray-100 flex items-center gap-2 text-[11px]">
            <span className="text-gray-500">Model:</span>
            <button
              onClick={() => setModel('gemini-2.5-flash-image')}
              className={`px-2.5 py-1 rounded-md font-medium ${model === 'gemini-2.5-flash-image' ? 'bg-violet-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
            >
              Flash (hızlı)
            </button>
            <button
              onClick={() => setModel('gemini-3-pro-image-preview')}
              className={`px-2.5 py-1 rounded-md font-medium ${model === 'gemini-3-pro-image-preview' ? 'bg-violet-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
            >
              Pro (yüksek kalite)
            </button>
            <span className="ml-auto text-gray-400">Flash daha kararlı; sonuç yetersizse Pro'ya geç.</span>
          </div>
        </div>

        {/* Global error */}
        {globalError && (
          <div className="mb-5 flex items-start gap-2 bg-red-50 border border-red-200 text-red-700 rounded-xl px-4 py-3 text-sm">
            <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
            <span>{globalError}</span>
          </div>
        )}

        {/* Items grid */}
        {hasItems && (
          <div className="space-y-4">
            {items.map((it) => {
              const badge = STATUS_BADGE[it.status] || STATUS_BADGE.idle;
              return (
                <div key={it.id} className="bg-white rounded-2xl ring-1 ring-gray-200 shadow-sm overflow-hidden">
                  <div className="px-4 py-2.5 border-b border-gray-100 flex items-center gap-3">
                    <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${badge.cls}`}>{badge.text}</span>
                    <span className="text-xs text-gray-700 truncate flex-1" title={it.name}>{it.name}</span>
                    {it.source && (
                      <span className="text-[10px] text-gray-400 hidden sm:inline">
                        {it.source.originalWidth}×{it.source.originalHeight}px
                      </span>
                    )}
                    {it.result?.presetLabel && (
                      <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-violet-50 text-violet-700">
                        {it.result.presetLabel}
                      </span>
                    )}
                    <button
                      onClick={() => removeItem(it.id)}
                      disabled={it.status === 'processing' || it.status === 'queued'}
                      className="text-gray-400 hover:text-red-600 disabled:opacity-30"
                      title="Kaldır"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2">
                    {/* Source */}
                    <div className="bg-gray-50 min-h-[260px] aspect-[3/4] md:aspect-auto flex items-center justify-center border-b md:border-b-0 md:border-r border-gray-100">
                      {it.source ? (
                        <img src={it.source.preview} alt="kaynak" className="max-w-full max-h-[400px] object-contain" />
                      ) : (
                        <div className="text-xs text-red-600 px-4 text-center">{it.error || 'Yüklenemedi'}</div>
                      )}
                    </div>

                    {/* Result */}
                    <div className="bg-gray-50 min-h-[260px] aspect-[3/4] md:aspect-auto flex items-center justify-center relative">
                      {it.status === 'processing' && (
                        <div className="flex flex-col items-center gap-2 text-gray-500">
                          <Loader2 className="w-7 h-7 animate-spin text-violet-500" />
                          <p className="text-xs">İşleniyor…</p>
                          <p className="text-[10px] text-gray-400">15–40 sn</p>
                        </div>
                      )}
                      {it.status === 'queued' && (
                        <div className="flex flex-col items-center gap-2 text-gray-400">
                          <Loader2 className="w-6 h-6 animate-spin" />
                          <p className="text-xs">Sırada bekliyor…</p>
                        </div>
                      )}
                      {it.status === 'error' && (
                        <div className="flex flex-col items-center gap-2 text-red-600 px-4 text-center">
                          <AlertCircle className="w-6 h-6" />
                          <p className="text-xs leading-relaxed">{it.error}</p>
                        </div>
                      )}
                      {it.status === 'idle' && !it.result && (
                        <div className="text-center text-gray-400 px-4">
                          <Wand2 className="w-7 h-7 mx-auto mb-2" />
                          <p className="text-xs">Yukarıdan bir aksiyon seç.</p>
                        </div>
                      )}
                      {it.result && it.status === 'done' && (
                        <img src={it.result.dataUrl} alt="sonuç" className="max-w-full max-h-[400px] object-contain" />
                      )}
                    </div>
                  </div>

                  {/* Footer per item */}
                  <div className="px-4 py-2.5 border-t border-gray-100 flex flex-wrap items-center gap-3">
                    {it.result && it.status === 'done' && (
                      <span className="text-[11px] text-emerald-700 inline-flex items-center gap-1.5">
                        <CheckCircle2 className="w-3.5 h-3.5" />
                        Çıktı orijinal boyutta: {it.source?.originalWidth}×{it.source?.originalHeight}px
                      </span>
                    )}
                    <div className="ml-auto flex items-center gap-2">
                      {it.result && (
                        <>
                          <button
                            onClick={() => applyResultAsSource(it.id)}
                            disabled={loading}
                            className="text-[11px] font-medium text-violet-600 hover:text-violet-800 disabled:opacity-50 inline-flex items-center gap-1"
                            title="Sonucu kaynak yap, başka aksiyon dene"
                          >
                            <RefreshCw className="w-3.5 h-3.5" /> Tekrar düzenle
                          </button>
                          <button
                            onClick={() => downloadOne(it)}
                            className="inline-flex items-center gap-1 text-xs font-semibold bg-emerald-600 hover:bg-emerald-700 text-white px-3 py-1.5 rounded-lg"
                          >
                            <Download className="w-3.5 h-3.5" /> İndir
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
